var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/hooks/useRemoteCursorOverlayPositions.tsx
import {
  CursorEditor,
  relativeRangeToSlateRange
} from "@slate-yjs/core";
import {
  useCallback as useCallback2,
  useEffect as useEffect2,
  useLayoutEffect,
  useMemo,
  useRef as useRef2,
  useState as useState2
} from "react";
import { useSlate } from "slate-react";

// src/hooks/useRequestReRender.ts
import { useCallback, useEffect, useRef, useState } from "react";
function useRequestReRender() {
  const [, setUpdateCounter] = useState(0);
  const animationFrameRef = useRef(null);
  const requestReRender = useCallback((immediate = false) => {
    if (animationFrameRef.current && !immediate) {
      return;
    }
    if (!immediate) {
      animationFrameRef.current = requestAnimationFrame(() => {
        setUpdateCounter((state) => state + 1);
        animationFrameRef.current = null;
      });
      return;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setUpdateCounter((state) => state + 1);
  }, []);
  useEffect(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  });
  useEffect(() => () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);
  return requestReRender;
}

// src/utils/selection.ts
import { Editor, Path, Range, Text } from "slate";
import { ReactEditor } from "slate-react";
function getSelectionRects(editor, range, xOffset, yOffset) {
  const [start, end] = Range.edges(range);
  const domRange = ReactEditor.toDOMRange(editor, range);
  const selectionRects = [];
  const nodeIterator = Editor.nodes(editor, { at: range, match: Text.isText });
  for (const [node, path] of nodeIterator) {
    const domNode = ReactEditor.toDOMNode(editor, node);
    const isStartNode = Path.equals(path, start.path);
    const isEndNode = Path.equals(path, end.path);
    let clientRects = null;
    if (isStartNode || isEndNode) {
      const nodeRange = document.createRange();
      nodeRange.selectNode(domNode);
      if (isStartNode) {
        nodeRange.setStart(domRange.startContainer, domRange.startOffset);
      }
      if (isEndNode) {
        nodeRange.setEnd(domRange.endContainer, domRange.endOffset);
      }
      clientRects = nodeRange.getClientRects();
    } else {
      clientRects = domNode.getClientRects();
    }
    for (let i = 0; i < clientRects.length; i++) {
      const clientRect = clientRects.item(i);
      if (!clientRect) {
        continue;
      }
      selectionRects.push({
        width: clientRect.width,
        height: clientRect.height,
        top: clientRect.top - yOffset,
        left: clientRect.left - xOffset
      });
    }
  }
  return selectionRects;
}
function getCaretPosition(selectionRects, range) {
  const isCollapsed = range && Range.isCollapsed(range);
  const isBackward = range && Range.isBackward(range);
  const anchorRect = selectionRects[isBackward ? 0 : selectionRects.length - 1];
  if (!anchorRect) {
    return null;
  }
  return {
    height: anchorRect.height,
    top: anchorRect.top,
    left: anchorRect.left + (isBackward || isCollapsed ? 0 : anchorRect.width)
  };
}

// src/hooks/useRemoteCursorOverlayPositions.tsx
var RANGE_CACHE = /* @__PURE__ */ new WeakMap();
var FROZEN_EMPTY_ARRAY = Object.freeze([]);
function getRange(editor, relativeRange) {
  let cache = RANGE_CACHE.get(editor.children);
  if (!cache) {
    cache = /* @__PURE__ */ new WeakMap();
    RANGE_CACHE.set(editor.children, cache);
  }
  const cachedRange = cache.get(relativeRange);
  if (cachedRange !== void 0) {
    return cachedRange;
  }
  const range = relativeRangeToSlateRange(editor.sharedRoot, editor, relativeRange);
  cache.set(relativeRange, range);
  return range;
}
function useRemoteCursorOverlayPositions({
  containerRef,
  refreshOnResize = true
} = {}) {
  const editor = useSlate();
  const requestReRender = useRequestReRender();
  const selectionRectCache = useRef2(/* @__PURE__ */ new WeakMap());
  const [cursorStates, setCursorStates] = useState2({});
  const [selectionRects, setSelectionRects] = useState2({});
  const updateCursors = useCallback2((clientIds) => {
    setCursorStates((current) => {
      if (!clientIds) {
        return CursorEditor.cursorStates(editor);
      }
      const updatedStates = Object.fromEntries(clientIds.map((id) => [id, CursorEditor.cursorState(editor, id)]));
      return Object.fromEntries(Object.entries(__spreadValues(__spreadValues({}, current), updatedStates)).filter(([, value]) => value !== null));
    });
  }, [editor]);
  useEffect2(() => {
    const handleChange = ({
      added,
      removed,
      updated
    }) => {
      updateCursors([...added, ...removed, ...updated]);
      requestReRender();
    };
    CursorEditor.on(editor, "change", handleChange);
    return () => CursorEditor.off(editor, "change", handleChange);
  }, [editor, requestReRender, updateCursors]);
  useLayoutEffect(() => {
    if ((containerRef == null ? void 0 : containerRef.current) === null) {
      return;
    }
    let xOffset = 0;
    let yOffset = 0;
    if (containerRef) {
      const contentRect = containerRef.current.getBoundingClientRect();
      xOffset = contentRect.x;
      yOffset = contentRect.y;
    }
    let selectionRectsChanged = Object.keys(selectionRects).length !== Object.keys(cursorStates).length;
    const updated = Object.fromEntries(Object.entries(cursorStates).map(([key, state]) => {
      const range = state.relativeSelection && getRange(editor, state.relativeSelection);
      if (!range) {
        return [key, FROZEN_EMPTY_ARRAY];
      }
      const cached = selectionRectCache.current.get(range);
      if (cached) {
        return [key, cached];
      }
      const rects = getSelectionRects(editor, range, xOffset, yOffset);
      selectionRectsChanged = true;
      selectionRectCache.current.set(range, rects);
      return [key, rects];
    }));
    if (selectionRectsChanged) {
      setSelectionRects(updated);
    }
  });
  const cursors = useMemo(() => Object.entries(cursorStates).map(([clientId, state]) => {
    var _a;
    const selection = state.relativeSelection && getRange(editor, state.relativeSelection);
    const rects = (_a = selectionRects[clientId]) != null ? _a : FROZEN_EMPTY_ARRAY;
    const caretPosition = selection && getCaretPosition(rects, selection);
    return __spreadProps(__spreadValues({}, state), {
      selection,
      caretPosition,
      selectionRects: rects
    });
  }), [cursorStates, editor, selectionRects]);
  const refresh = useCallback2((sync = false) => {
    selectionRectCache.current = /* @__PURE__ */ new WeakMap();
    requestReRender(sync);
  }, [requestReRender]);
  useEffect2(() => {
    if (!refreshOnResize || !(containerRef == null ? void 0 : containerRef.current)) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => refresh());
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [containerRef, refresh, refreshOnResize]);
  return { refresh, cursors };
}
export {
  useRemoteCursorOverlayPositions
};
//# sourceMappingURL=index.js.map